import React, { useMemo } from "react";
import CoatingSettingsModal from "../modals/CoatingSettingsModal";
import ConfigureCoatingModal from "../modals/ConfigureCoatingModal";
import type { CoatingCell } from "../../../ThermalDesignTypes";
import "../styles/ThermalCoatingSection.scss";
import {
  NoCoatingIcon,
  MLICoatingIcon,
  OSRCoatingIcon,
  ToggleOffGrayIcon,
  ToggleOnGreenIcon,
  AdjustSettingsIcon,
  InfoIcon,
} from "../../../SatelliteProfileImgAssets/SatelliteProfileImgAssets";
import { useThermalDesign } from "../../../ThermalDesignContext";
import { useThermalCoatingToggle } from "../hooks/ThermalDesignerHooks";
import CustomToolTip from "../../../../../../CommonComponents/CustomToolTip";
import { useAnchoredPopup, ThermalCoatingInfoPopUp } from "./common/ThermalDesignerCommonComponents";

const ToggleButton: React.FC<{ enabled: boolean; onClick: () => void }> = ({ enabled, onClick }) => (
  <button className={`thermal-toggle-btn${enabled ? " active" : ""}`} type="button" onClick={onClick} aria-pressed={enabled}>
    {enabled ? <ToggleOnGreenIcon /> : <ToggleOffGrayIcon />}
  </button>
);

const ThermalCoatingSection: React.FC = () => {
  const { nodeSpecsOptions, setNodeSpecsOptions, openModal, setOpenModal } = useThermalDesign();
  
  // Find the selected node (external face)
  const selectedNode = useMemo(() => nodeSpecsOptions.find((n) => n.isSelected), [nodeSpecsOptions]);
  const isExternal = selectedNode && selectedNode.values && !selectedNode.values.node_isInternal;
  
  // Use the modularized hook for toggle management
  const { enabled, handleToggle } = useThermalCoatingToggle(selectedNode, nodeSpecsOptions, setNodeSpecsOptions);

  // Hook for coating info popup
  const { isOpen: showCoatingInfo, triggerRef: coatingInfoTriggerRef, toggle: handleToggleCoatingInfo, renderPopup: renderCoatingInfoPopup } = useAnchoredPopup();

  const TOTAL_CELLS = 9;

  const readCellsFromNode = (node: typeof selectedNode): CoatingCell[] => {
    const saved = node?.values?.coating?.cells;
    if (Array.isArray(saved) && saved.length === TOTAL_CELLS) return [...saved] as CoatingCell[];
    return Array(TOTAL_CELLS).fill("none") as CoatingCell[];
  };

  const [cells, setCells] = React.useState<CoatingCell[]>(() => readCellsFromNode(selectedNode));

  // Close configure modal and re-sync cells whenever the selected node/face changes
  React.useEffect(() => {
    if (openModal.isConfigureCoatingModalOpen) {
      setOpenModal((prev) => ({
        ...prev,
        isConfigureCoatingModalOpen: false,
      }));
    }
    setCells(readCellsFromNode(selectedNode));
  }, [selectedNode?.id]);

  // Helper to get or initialize coating config
  const getCoating = (type: "none" | "mli" | "osr") => {
    if (!selectedNode?.values?.coating) return { percentage: 0 };
    return selectedNode.values.coating[type] || { percentage: 0 };
  };

  // Fill All: fill all 9 cells with the active brush type
  // (moved to ConfigureCoatingModal)

  if (!isExternal) return null;

  return (
    <div className="thermal-coating-section thermal-coating-flex typography-montserrat">
      <div className="thermal-coating-header thermal-coating-header-flex">
        <div className="thermal-coating-toggle-row">
          <span className="thermal-coating-toggle">Thermal Coating</span>
          <ToggleButton enabled={enabled} onClick={handleToggle} />
        </div>
        <div className="material_info_trigger" ref={coatingInfoTriggerRef}>
          <CustomToolTip title="View coating info" placement="top">
            <button className="material_info_btn" onClick={handleToggleCoatingInfo}>
              <InfoIcon isActive={showCoatingInfo} />
            </button>
          </CustomToolTip>
        </div>
      </div>
      {renderCoatingInfoPopup(<ThermalCoatingInfoPopUp />)}
      {enabled && (
        <div className="thermal-coating-body thermal-coating-body-fullwidth">
          <div className="thermal-coating-stats-row">
            <div
              className={`coating-box none${openModal.isConfigureCoatingModalOpen && openModal.activeBrush === "none" ? " coating-box--active" : ""}`}
              onClick={() => setOpenModal((prev) => ({
                ...prev,
                isCustomMaterialModalOpen: false,
                isCoatingSettingsModalOpen: false,
                isConfigureCoatingModalOpen: true,
                activeBrush: "none",
              }))}
              style={{ cursor: "pointer" }}
            >
              <div className="coating-box-left">
                <div className="coating-icon">
                  <NoCoatingIcon />
                </div>
                <div className="coating-labels-and-data">
                  <div className="coating-label">
                    None: <span className="coating-value-box">{getCoating("none").percentage.toFixed(2)}%</span>
                  </div>
                  <div className="coating-data">&alpha; 0.00 &epsilon; 0.00</div>
                </div>
              </div>
            </div>
            <div
              className={`coating-box mli${openModal.isConfigureCoatingModalOpen && openModal.activeBrush === "mli" ? " coating-box--active" : ""}`}
              onClick={() => setOpenModal((prev) => ({
                ...prev,
                isCustomMaterialModalOpen: false,
                isCoatingSettingsModalOpen: false,
                isConfigureCoatingModalOpen: true,
                activeBrush: "mli",
              }))}
              style={{ cursor: "pointer" }}
            >
              <div className="coating-box-left">
                <div className="coating-icon">
                  <MLICoatingIcon />
                </div>
                <div className="coating-labels-and-data">
                  <div className="coating-label">
                    MLI: <span className="coating-value-box">{getCoating("mli").percentage.toFixed(2)}%</span>
                  </div>
                  <div className="coating-data">
                    &alpha; {(getCoating("mli").absorptivity ?? 0).toFixed(2)} &epsilon; {(getCoating("mli").emissivity ?? 0).toFixed(2)}
                  </div>
                </div>
              </div>
              <button className="coating-settings-btn" type="button" aria-label="Adjust Settings" onClick={(e) => { e.stopPropagation(); setOpenModal((prev) => ({
                ...prev,
                isCustomMaterialModalOpen: false,
                isConfigureCoatingModalOpen: false,
                isCoatingSettingsModalOpen: true,
                activeCoatingType: "mli",
              })); }}>
                <AdjustSettingsIcon />
              </button>
            </div>
            <div
              className={`coating-box osr${openModal.isConfigureCoatingModalOpen && openModal.activeBrush === "osr" ? " coating-box--active" : ""}`}
              onClick={() => setOpenModal((prev) => ({
                ...prev,
                isCustomMaterialModalOpen: false,
                isCoatingSettingsModalOpen: false,
                isConfigureCoatingModalOpen: true,
                activeBrush: "osr",
              }))}
              style={{ cursor: "pointer" }}
            >
              <div className="coating-box-left">
                <div className="coating-icon">
                  <OSRCoatingIcon />
                </div>
                <div className="coating-labels-and-data">
                  <div className="coating-label">
                    OSR: <span className="coating-value-box">{getCoating("osr").percentage.toFixed(2)}%</span>
                  </div>
                  <div className="coating-data">
                    &alpha; {(getCoating("osr").absorptivity ?? 0).toFixed(2)} &epsilon; {(getCoating("osr").emissivity ?? 0).toFixed(2)}
                  </div>
                </div>
              </div>
              <button className="coating-settings-btn" type="button" aria-label="Adjust Settings" onClick={(e) => { e.stopPropagation(); setOpenModal((prev) => ({
                ...prev,
                isCustomMaterialModalOpen: false,
                isConfigureCoatingModalOpen: false,
                isCoatingSettingsModalOpen: true,
                activeCoatingType: "osr",
              })); }}>
                <AdjustSettingsIcon />
              </button>
            </div>
            {openModal.isCoatingSettingsModalOpen && <CoatingSettingsModal onClose={() => setOpenModal((prev) => ({ ...prev, isCoatingSettingsModalOpen: false }))} />}
            {openModal.isConfigureCoatingModalOpen && (
              <ConfigureCoatingModal
                onClose={() => setOpenModal((prev) => ({ ...prev, isConfigureCoatingModalOpen: false }))}
                cells={cells}
                setCells={setCells}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ThermalCoatingSection;
